import {
  CalendarOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Drawer, Space, Tooltip, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText, formatDate } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  NotificationsResponse,
  SpeakerFreeWeekRow,
  SpeakerSearchRow,
} from "../api/types";
import { CandidateInfoPanel } from "../components/CandidateInfoPanel";
import { DataTable } from "../components/DataTable";
import { WeekTimeDialog } from "../components/WeekTimeDialog";
import type { ExportableColumn } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "speaker_request" → /local_assignation/speaker_requests.
 *
 * Finding a speaker beyond the own congregation: the external speakers of every
 * circumscription the coordinator collaborates with, in a language they
 * collaborate in. A search term is required — the endpoint is for finding a
 * particular speaker or talk, not for browsing everyone.
 *
 * A hit is a *pair* of speaker and talk. Picking one opens the weeks they could
 * take, each with the server's own wording for what that week would mean:
 * still free, free but the speaker would rather not, or already theirs with a
 * different talk.
 */
export function SpeakerRequestPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();

  const [filters, setFilters] = useState<FilterValues>({});
  const [weeksOf, setWeeksOf] = useState<SpeakerSearchRow | null>(null);

  const searched = Object.keys(filters).length > 0;

  const results = useQuery({
    queryKey: ["local_assignation", "speaker_search", filters],
    queryFn: async () => {
      const answer = await apiFetch<
        ListResponse<SpeakerSearchRow> & { notifications?: NotificationsResponse["notifications"] }
      >(withQuery("/local_assignation/speaker_search", filters));
      // The endpoint answers 200 with a notice when no term was given; that is
      // guidance, not an error, so it goes through the usual channel.
      if (answer.notifications) notify(answer.notifications);
      return answer;
    },
    enabled: searched,
  });

  const fields = useMemo(
    () => [
      { name: "firstname", label: t("activerecord.attributes.speaker.first_name") },
      { name: "lastname", label: t("activerecord.attributes.speaker.last_name") },
      { name: "speech_number", label: t("activerecord.attributes.speech.schema") },
      { name: "speech_title", label: t("activerecord.attributes.speech.title") },
    ],
    [t],
  );

  const columns = useMemo<ExportableColumn<SpeakerSearchRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 140,
        sorter: (a, b) => compareText(a.first_name, b.first_name),
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 140,
        sorter: (a, b) => compareText(a.last_name, b.last_name),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "speech_schema",
        width: 90,
        align: "right",
        sorter: (a, b) => compareNumeric(a.speech_schema, b.speech_schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "speech_language",
        width: 90,
      },
      {
        title: t("activerecord.models.circumscription"),
        dataIndex: "speaker_circumscription",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 170,
        ellipsis: true,
      },
      {
        title: t("local_assignation.last_visit"),
        dataIndex: "last_visit",
        width: 140,
        render: (value: string | null) =>
          value ? (
            formatDate(value)
          ) : (
            <Typography.Text type="secondary">
              {t("local_assignation.never_requested")}
            </Typography.Text>
          ),
        // An empty cell would not say the same thing as "never".
        exportValue: (row) =>
          row.last_visit ? formatDate(row.last_visit) : t("local_assignation.never_requested"),
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.user.zip_code"),
        dataIndex: "zip_code",
        width: 90,
      },
      {
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_value, row) => (
          <Tooltip title={t("local_assignation.available_weeks")}>
            <Button size="small" icon={<CalendarOutlined />} onClick={() => setWeeksOf(row)} />
          </Tooltip>
        ),
      },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.find_speaker")}>
      <FilterBar fields={fields} onApply={setFilters} loading={results.isFetching} />

      {!searched && (
        <Alert
          type="info"
          showIcon
          title={t("local_assignation.filter_speaker_speech")}
          style={{ marginBottom: 16 }}
        />
      )}

      {searched && (
        <DataTable<SpeakerSearchRow>
          columns={columns}
          rows={results.data?.data}
          exportAs={t("navigation.find_speaker")}
          loading={results.isPending}
          error={results.error}
        />
      )}

      {weeksOf && <FreeWeeksDrawer speaker={weeksOf} onClose={() => setWeeksOf(null)} />}
    </Card>
  );
}

/**
 * The weeks this speaker could take. Assigning goes through the same endpoint
 * the public-talk screen uses, so the same rules apply and the same
 * notifications come back.
 */
function FreeWeeksDrawer({
  speaker,
  onClose,
}: {
  speaker: SpeakerSearchRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  // The panel describes this pair *in a given week* — distance and the rules
  // behind it depend on the date, so it follows the week that is selected. The
  // old screen bound it the same way: the search grid picked the pair, the week
  // grid picked the week.
  const [selected, setSelected] = useState<SpeakerFreeWeekRow | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<SpeakerFreeWeekRow | null>(null);

  const queryKey = useMemo(
    () => ["local_assignation", "speaker_search", "weeks", speaker.id],
    [speaker.id],
  );

  const weeks = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<SpeakerFreeWeekRow>>(
        `/local_assignation/speaker_search/${speaker.id}/weeks`,
      ),
  });

  const assign = useMutation({
    mutationFn: (week: SpeakerFreeWeekRow) =>
      apiFetch<NotificationsResponse>(
        `/local_assignation/events/${week.week_begin}/assignment`,
        { method: "PUT", body: { speaker_speech_id: speaker.id } },
      ),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
      void queryClient.invalidateQueries({ queryKey: ["archive"] });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const columns = useMemo<ExportableColumn<SpeakerFreeWeekRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        // A single icon, so it stays beside the date; the date only has to be
        // kept from breaking around it.
        render: (value: string, row) => (
          <Space size={4}>
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {/* The server flags a pairing that is worth a second thought —
                distance, notice period or one of the absence rules. */}
            {!row.notifications_ok && (
              <Tooltip title={t("dialog.considerations")}>
                <WarningOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </Space>
        ),
        exportValue: (row) =>
          row.notifications_ok
            ? row.week_label
            : `${row.week_label} (${t("dialog.considerations")})`,
      },
      {
        title: t("activerecord.attributes.local_assignation.observations"),
        dataIndex: "observations",
        width: 240,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.action"),
        dataIndex: "action",
        width: 240,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_value, row) => (
          <Space size={4}>
            <Button
              size="small"
              type="primary"
              disabled={assign.isPending}
              // The row itself opens the info panel; assigning is its own act.
              onClick={(event) => {
                event.stopPropagation();
                assign.mutate(row);
              }}
            >
              {t("local_assignation.select_speaker")}
            </Button>

            {/* Once the week is this speaker's, day and time can be moved for
                it — the old grid let both be typed into the row itself, and
                only on the weeks that carry an assignation. */}
            {row.assigned && (
              <Tooltip title={t("form.update")}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={assign.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditing(row);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, assign],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={panelOpen ? "min(1600px, 96vw)" : "min(1100px, 96vw)"}
      destroyOnHidden
      title={
        <Typography.Text>
          {[speaker.first_name, speaker.last_name, speaker.speech_schema]
            .filter(Boolean)
            .join(" · ")}
        </Typography.Text>
      }
      extra={
        <Button
          icon={panelOpen ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => setPanelOpen((open) => !open)}
        >
          {t("local_assignation.search_speaker_info")}
        </Button>
      }
    >
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <DataTable<SpeakerFreeWeekRow>
            columns={columns}
            rows={weeks.data?.data}
            exportAs={[speaker.first_name, speaker.last_name, speaker.speech_schema]
              .filter(Boolean)
              .join(" · ")}
            loading={weeks.isPending || assign.isPending}
            error={weeks.error}
            rowKey="week_begin"
            onRowClick={(row) => {
              setSelected(row);
              setPanelOpen(true);
            }}
            rowClassName={(row) =>
              row.week_begin === selected?.week_begin ? "free-week-selected" : ""
            }
          />
        </div>

        {panelOpen && (
          <div
            style={{
              width: 420,
              flexShrink: 0,
              borderInlineStart: "1px solid #f0f0f0",
              paddingInlineStart: 16,
              maxHeight: "calc(100vh - 200px)",
              overflow: "auto",
            }}
          >
            <CandidateInfoPanel
              weekBegin={selected?.week_begin ?? null}
              speakerSpeechId={speaker.id}
              placeholder={t("local_assignation.available_weeks")}
            />
          </div>
        )}
      </div>

      <style>{`.free-week-selected > td { background: rgba(22, 119, 255, 0.06); }`}</style>

      {editing && (
        <WeekTimeDialog
          week={editing}
          onClose={() => setEditing(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
            void queryClient.invalidateQueries({ queryKey: ["archive"] });
          }}
        />
      )}
    </Drawer>
  );
}
